import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Shield, AlertTriangle, Database, Zap, Users, BookOpen,
  ChevronRight, CheckCircle, Clock, ArrowLeft, XCircle,
  Info, AlertCircle, RefreshCw, Pause, Bell, UserCheck,
} from 'lucide-react';
import { getEscalationRules, getExceptionCodes } from '../../lib/slaCatalogueRegistry';
import { getAllStages } from '../../lib/leadPipelineRegistry';
import { getAllRoles, computeRACIMatrix } from '../../lib/rolesRegistry';

// ─── Data Quality Policy ────────────────────────────────────────────────────
function DataQualityPolicy({ isAr }: { isAr: boolean }) {
  const stages = getAllStages();

  const coreRules = [
    { en: 'Mobile number must be unique OR linked to existing customer record', ar: 'رقم الجوال يجب أن يكون فريدًا أو مرتبطًا بسجل عميل موجود' },
    { en: 'Source channel must be from approved picklist (no "Other" without approval)', ar: 'قناة المصدر يجب أن تكون من القائمة المعتمدة (لا "أخرى" بدون موافقة)' },
    { en: 'Brand interest is required before assignment can complete', ar: 'الاهتمام بالعلامة التجارية مطلوب قبل اكتمال التعيين' },
    { en: 'If walk-in source: branch must be specified', ar: 'إذا كان المصدر زيارة مباشرة: يجب تحديد الفرع' },
    { en: 'If OEM portal source: dealer code must match Almajdouie', ar: 'إذا كان المصدر بوابة OEM: كود الوكيل يجب أن يطابق المجدوعي' },
    { en: 'All active leads must always have a next action scheduled', ar: 'جميع العملاء النشطين يجب أن يكون لديهم إجراء تالٍ مجدول دائمًا' },
    { en: 'Reason codes are mandatory when moving to any outcome stage', ar: 'رموز الأسباب إلزامية عند الانتقال لأي مرحلة نتيجة' },
    { en: '100% source attribution – every lead must have a valid source', ar: '100% نسبة تتبع المصدر – كل عميل يجب أن يكون له مصدر صالح' },
  ];

  const preStageFields = [
    { field: 'full_name', type: 'Text', required: true, validation: 'Min 2 characters' },
    { field: 'mobile', type: 'Phone', required: true, validation: 'Saudi format +966 5XXXXXXXX' },
    { field: 'email', type: 'Email', required: false, validation: 'Valid email format' },
    { field: 'source_channel', type: 'Picklist', required: true, validation: 'From approved list' },
    { field: 'brand_interest', type: 'Picklist', required: true, validation: 'Changan / Hyundai / Stellantis' },
    { field: 'language_preference', type: 'Picklist', required: true, validation: 'Arabic / English' },
    { field: 'whatsapp_consent', type: 'Checkbox', required: true, validation: 'Default: false' },
    { field: 'branch_assignment', type: 'Lookup', required: true, validation: 'Auto-populated by system' },
    { field: 'assigned_to', type: 'Lookup', required: true, validation: 'BDC Agent' },
  ];

  const kpiTargets = [
    { kpi: 'Lead capture completeness', target: '>80% data at entry', ar: 'اكتمال بيانات الالتقاط', targetAr: '>80% عند الإدخال' },
    { kpi: 'Source attribution accuracy', target: '100%', ar: 'دقة تتبع المصدر', targetAr: '100%' },
    { kpi: 'WhatsApp consent capture', target: '>90%', ar: 'التقاط موافقة WhatsApp', targetAr: '>90%' },
    { kpi: 'Duplicate detection rate', target: '>95%', ar: 'معدل اكتشاف التكرار', targetAr: '>95%' },
    { kpi: 'Data completeness post-qualification', target: '>90%', ar: 'اكتمال البيانات بعد التأهيل', targetAr: '>90%' },
    { kpi: 'Qualification completion rate', target: '>95%', ar: 'معدل اكتمال التأهيل', targetAr: '>95%' },
  ];

  return (
    <div className="space-y-8">
      {/* Core Rules */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600" />
          {isAr ? 'قواعد جودة البيانات الأساسية' : 'Core Data Quality Rules'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {coreRules.map((r, i) => (
            <div key={i} className="flex items-start gap-2.5 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">{isAr ? r.ar : r.en}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Mandatory Fields Table */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-600" />
          {isAr ? 'حقول CRM الإلزامية – مرحلة الإدخال' : 'Mandatory CRM Fields – Lead Capture Stage'}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Field</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Type</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Required</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Validation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {preStageFields.map((f) => (
                <tr key={f.field} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-blue-700 font-semibold">{f.field}</td>
                  <td className="px-4 py-3 text-gray-600">{f.type}</td>
                  <td className="px-4 py-3">
                    {f.required
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full"><CheckCircle className="w-3 h-3" /> Yes</span>
                      : <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">No</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{f.validation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Stage-by-Stage CRM fields summary */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-blue-600" />
          {isAr ? 'متطلبات البيانات حسب المرحلة' : 'Data Requirements by Pipeline Stage'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {stages.map((stage) => (
            <Link
              key={stage.meta.id}
              to={`/pipeline/stage/${stage.meta.id}`}
              className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: stage.meta.color || '#6366f1' }}
                >
                  {stage.meta.order}
                </span>
                <span className="text-sm font-semibold text-gray-800 group-hover:text-blue-700">
                  {isAr ? stage.meta.titleAr : stage.meta.title}
                </span>
              </div>
              <div className="text-xs text-gray-500">
                {stage.mandatoryFields?.length ?? 0} {isAr ? 'حقل إلزامي' : 'mandatory fields'}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* KPI Targets */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-600" />
          {isAr ? 'مؤشرات الأداء الرئيسية لجودة البيانات' : 'Data Quality KPI Targets'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kpiTargets.map((k, i) => (
            <div key={i} className="p-4 bg-blue-50 rounded-xl border border-blue-100">
              <div className="text-sm font-semibold text-gray-800 mb-1">{isAr ? k.ar : k.kpi}</div>
              <div className="text-xl font-bold text-blue-700">{isAr ? k.targetAr : k.target}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── Escalation Policy ───────────────────────────────────────────────────────
function EscalationPolicy({ isAr }: { isAr: boolean }) {
  const rules = getEscalationRules();

  const triggerColors: Record<string, string> = {
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    breach:  'bg-red-100 text-red-800 border-red-200',
    critical: 'bg-red-200 text-red-900 border-red-300',
  };

  const triggerIcons: Record<string, React.ReactNode> = {
    warning: <AlertCircle className="w-4 h-4" />,
    breach:  <AlertTriangle className="w-4 h-4" />,
    critical: <XCircle className="w-4 h-4" />,
  };

  const chainSteps = [
    { pct: '80%', role: 'BDC Supervisor', roleAr: 'مشرف مركز التطوير', action: 'Visual alert + supervisor dashboard highlight', actionAr: 'تنبيه مرئي + تمييز لوحة المشرف', color: 'bg-yellow-500' },
    { pct: '100%', role: 'BDC Manager', roleAr: 'مدير مركز التطوير', action: 'Auto-reassign to backup agent + log breach', actionAr: 'إعادة تعيين تلقائي + تسجيل الخرق', color: 'bg-orange-500' },
    { pct: '2nd breach', role: 'Sales Manager', roleAr: 'مدير المبيعات', action: 'Root cause analysis + customer apology call', actionAr: 'تحليل السبب الجذري + مكالمة اعتذار', color: 'bg-red-600' },
  ];

  return (
    <div className="space-y-8">
      {/* Escalation Chain */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
          {isAr ? 'سلسلة التصعيد التلقائية' : 'Automatic Escalation Chain'}
        </h2>
        <div className="relative">
          {/* Connecting line */}
          <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gray-200 hidden sm:block" />
          <div className="space-y-4">
            {chainSteps.map((step, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-full ${step.color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0 z-10 relative`}>
                  {step.pct}
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    <UserCheck className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold text-gray-800">{isAr ? step.roleAr : step.role}</span>
                    <span className="text-xs text-gray-400">{isAr ? 'يتم إخطاره' : 'is notified'}</span>
                  </div>
                  <p className="text-sm text-gray-600">{isAr ? step.actionAr : step.action}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Escalation Rules */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
          <Bell className="w-5 h-5 text-orange-600" />
          {isAr ? 'قواعد التصعيد التفصيلية' : 'Detailed Escalation Rules'}
          <span className="ml-auto text-sm font-normal text-gray-400">{rules.length} {isAr ? 'قواعد' : 'rules'}</span>
        </h2>
        <div className="space-y-4">
          {rules.map((rule) => (
            <div key={rule.id} className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${triggerColors[rule.triggerType] || 'bg-gray-100 text-gray-700'}`}>
                    {triggerIcons[rule.triggerType]}
                    {rule.triggerType.toUpperCase()}
                    {rule.thresholdPercent ? ` – ${rule.thresholdPercent}%` : ''}
                  </span>
                  <h3 className="font-semibold text-gray-800">{isAr ? rule.nameAr : rule.name}</h3>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {rule.autoReassign && (
                    <span className="flex items-center gap-1 bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                      <RefreshCw className="w-3 h-3" /> {isAr ? 'إعادة تعيين تلقائي' : 'Auto-Reassign'}
                    </span>
                  )}
                </div>
              </div>
              <div className="px-5 py-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {isAr ? 'المحفز' : 'Trigger'}
                    </div>
                    <p className="text-sm text-gray-700">{isAr ? rule.triggerAr : rule.trigger}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {isAr ? 'يصعّد إلى' : 'Escalates To'}
                    </div>
                    <p className="text-sm font-semibold text-orange-700">{isAr ? rule.escalateToAr : rule.escalateTo}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {isAr ? 'الإجراءات' : 'Actions'}
                    </div>
                    <ul className="space-y-1">
                      {(isAr ? rule.actionsAr : rule.actions).map((a: string, i: number) => (
                        <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0 mt-1.5" />
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {rule.notifyChannels && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
                    <span className="text-xs text-gray-400">{isAr ? 'قنوات الإشعار:' : 'Notify via:'}</span>
                    {rule.notifyChannels.map((ch: string) => (
                      <span key={ch} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-mono">{ch}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── Exception Handling Policy ───────────────────────────────────────────────
function ExceptionsPolicy({ isAr }: { isAr: boolean }) {
  const codes = getExceptionCodes();

  const behaviorMeta: Record<string, { label: string; labelAr: string; color: string; icon: React.ReactNode }> = {
    pause:  { label: 'Pause SLA', labelAr: 'إيقاف الاتفاقية مؤقتًا', color: 'bg-yellow-100 text-yellow-800', icon: <Pause className="w-3.5 h-3.5" /> },
    exempt: { label: 'Exempt from SLA', labelAr: 'إعفاء من الاتفاقية', color: 'bg-gray-100 text-gray-700', icon: <XCircle className="w-3.5 h-3.5" /> },
    reset:  { label: 'Reset SLA Timer', labelAr: 'إعادة تعيين مؤقت الاتفاقية', color: 'bg-blue-100 text-blue-800', icon: <RefreshCw className="w-3.5 h-3.5" /> },
  };

  return (
    <div className="space-y-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {Object.entries(behaviorMeta).map(([key, meta]) => {
          const count = codes.filter((c) => c.slaBehavior === key).length;
          return (
            <div key={key} className={`rounded-xl p-4 border ${meta.color.replace('text-', 'border-').replace('bg-', 'border-')} bg-white shadow-sm`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`p-1.5 rounded-lg ${meta.color}`}>{meta.icon}</span>
                <span className="font-semibold text-gray-800">{isAr ? meta.labelAr : meta.label}</span>
              </div>
              <div className="text-3xl font-bold text-gray-900">{count}</div>
              <div className="text-xs text-gray-500 mt-0.5">{isAr ? 'رموز استثناء' : 'exception codes'}</div>
            </div>
          );
        })}
      </div>

      {/* All Exception Codes */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
          <Zap className="w-5 h-5 text-purple-600" />
          {isAr ? 'رموز الاستثناء الكاملة' : 'Complete Exception Code Reference'}
          <span className="ml-auto text-sm font-normal text-gray-400">{codes.length} {isAr ? 'رموز' : 'codes'}</span>
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {codes.map((code) => {
            const beh = behaviorMeta[code.slaBehavior];
            return (
              <div key={code.code} className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold bg-purple-100 text-purple-700 px-2 py-1 rounded-lg">{code.code}</span>
                    <h3 className="font-semibold text-gray-800">{isAr ? code.labelAr : code.label}</h3>
                  </div>
                  {beh && (
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${beh.color}`}>
                      {beh.icon}
                      {isAr ? beh.labelAr : beh.label}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-3">{isAr ? code.descriptionAr : code.description}</p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="text-gray-400">{isAr ? 'صالح في:' : 'Valid for:'}</span>
                  {code.validForStages.map((s: string) => (
                    <span key={s} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-mono">{s === 'all' ? (isAr ? 'الكل' : 'All Stages') : s}</span>
                  ))}
                </div>
                {code.requiresApproval && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg">
                    <Info className="w-3.5 h-3.5 flex-shrink-0" />
                    {isAr ? `يتطلب موافقة: ${code.approverAr || code.approver}` : `Requires approval from: ${code.approver}`}
                  </div>
                )}
                {code.requiredFields && code.requiredFields.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {code.requiredFields.map((f: string) => (
                      <span key={f} className="font-mono text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{f}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ─── Lead Handling Policy ────────────────────────────────────────────────────
function LeadHandlingPolicy({ isAr }: { isAr: boolean }) {
  const stages = getAllStages();

  const coreRules = [
    { en: 'Single Owner Rule: Every lead must have exactly one assigned owner at all times', ar: 'قاعدة المالك الواحد: كل عميل يجب أن يكون له مالك واحد فقط في جميع الأوقات' },
    { en: '8-Attempt Cadence: All leads in Contacting stage must complete full 8-attempt cadence before Unreachable', ar: 'جدول 8 محاولات: جميع العملاء في مرحلة التواصل يجب أن يكملوا الجدول الكامل قبل "لا يمكن الوصول"' },
    { en: 'Next Action Mandatory: Active leads must always have a next scheduled action', ar: 'الإجراء التالي إلزامي: العملاء النشطون يجب أن يكون لديهم دائمًا إجراء تالٍ مجدول' },
    { en: 'Channel Mix Required: At least 2 different channels per cadence (call + WhatsApp/SMS)', ar: 'تنويع القنوات مطلوب: على الأقل قناتان مختلفتان لكل جدول (مكالمة + WhatsApp/SMS)' },
    { en: 'Reason Codes Mandatory: All stage transitions to outcomes require a documented reason code', ar: 'رموز الأسباب إلزامية: جميع انتقالات المرحلة للنتائج تتطلب رمز سبب موثقًا' },
    { en: 'Lead Cannot Skip Stages: Sequential progression enforced by CRM validation', ar: 'لا يمكن تخطي المراحل: التقدم التسلسلي مفروض بواسطة التحقق في CRM' },
    { en: 'SLA Timer Starts Immediately: Upon stage entry, SLA clock begins automatically', ar: 'يبدأ مؤقت الاتفاقية فورًا: عند دخول المرحلة، تبدأ ساعة الاتفاقية تلقائيًا' },
    { en: 'Handover with Full Context: Stage handovers must include notes, history, and next action', ar: 'التسليم بكامل السياق: تسليم المرحلة يجب أن يتضمن الملاحظات والتاريخ والإجراء التالي' },
  ];

  const cadenceSteps = [
    { attempt: 1, timing: 'Day 1, 0h', channel: 'Call', action: 'First contact attempt', ar: 'محاولة الاتصال الأولى' },
    { attempt: 2, timing: 'Day 1, +2h', channel: 'WhatsApp', action: 'Follow-up template message', ar: 'رسالة نموذج المتابعة' },
    { attempt: 3, timing: 'Day 1, +4h', channel: 'Call', action: 'Mid-day attempt', ar: 'محاولة منتصف اليوم' },
    { attempt: 4, timing: 'Day 1, 4:30 PM', channel: 'Call', action: 'End-of-day attempt', ar: 'محاولة نهاية اليوم' },
    { attempt: 5, timing: 'Day 2, 8:30 AM', channel: 'Call', action: 'First-thing morning attempt', ar: 'محاولة أول الصباح' },
    { attempt: 6, timing: 'Day 2, 12:00 PM', channel: 'WhatsApp', action: 'Value-add message with offer', ar: 'رسالة قيمة مضافة مع عرض' },
    { attempt: 7, timing: 'Day 2, 4:00 PM', channel: 'Call', action: 'Afternoon attempt', ar: 'محاولة بعد الظهر' },
    { attempt: 8, timing: 'Day 3, 10:00 AM', channel: 'SMS + WhatsApp', action: 'Final outreach with deadline', ar: 'التواصل الأخير مع موعد نهائي' },
  ];

  const channelColors: Record<string, string> = {
    'Call': 'bg-blue-100 text-blue-700',
    'WhatsApp': 'bg-green-100 text-green-700',
    'SMS': 'bg-purple-100 text-purple-700',
    'SMS + WhatsApp': 'bg-teal-100 text-teal-700',
  };

  return (
    <div className="space-y-8">
      {/* Core Rules */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-green-600" />
          {isAr ? 'قواعد المعالجة الأساسية' : 'Core Lead Handling Rules'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {coreRules.map((r, i) => (
            <div key={i} className="flex items-start gap-2.5 p-3 bg-green-50 rounded-lg border border-green-100">
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">{isAr ? r.ar : r.en}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 8-Attempt Cadence */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-green-600" />
          {isAr ? 'جدول المحاولات الثماني الموحد' : 'Standard 8-Attempt Contact Cadence'}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-700">#</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{isAr ? 'التوقيت' : 'Timing'}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{isAr ? 'القناة' : 'Channel'}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{isAr ? 'الإجراء' : 'Action'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cadenceSteps.map((step) => (
                <tr key={step.attempt} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold inline-flex items-center justify-center">{step.attempt}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">{step.timing}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${channelColors[step.channel] || 'bg-gray-100 text-gray-700'}`}>
                      {step.channel}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{isAr ? step.ar : step.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5" />
          {isAr ? 'جميع المحاولات خلال ساعات BDC (08:00-17:00). الردود خارج الساعات تُعالج تلقائيًا.' : 'All attempts within BDC hours (08:00-17:00). After-hours responses handled by automation.'}
        </p>
      </section>

      {/* Stage Handover Rules */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <ArrowLeft className="w-5 h-5 text-green-600" />
          {isAr ? 'قواعد تسليم المرحلة' : 'Stage Handover Rules'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {stages.slice(0, 6).map((stage) => (
            <div key={stage.meta.id} className="p-4 rounded-xl bg-gray-50 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: stage.meta.color || '#6366f1' }}
                >
                  {stage.meta.order}
                </span>
                <span className="text-sm font-semibold text-gray-800">{isAr ? stage.meta.titleAr : stage.meta.title}</span>
              </div>
              <div className="text-xs text-gray-600">
                {isAr ? `المالك: ${stage.meta.ownerAr || stage.meta.owner}` : `Owner: ${stage.meta.owner}`}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── SLA Compliance Policy ───────────────────────────────────────────────────
function SLACompliancePolicy({ isAr }: { isAr: boolean }) {
  const stages = getAllStages();

  const channelTargets = [
    { channel: 'Website Leads', channelAr: 'عملاء الموقع', target: '< 3 min', priority: 'critical', color: 'bg-red-100 text-red-700' },
    { channel: 'WhatsApp', channelAr: 'واتساب', target: '< 5 min', priority: 'critical', color: 'bg-red-100 text-red-700' },
    { channel: 'OEM Portal', channelAr: 'بوابة OEM', target: '< 10 min', priority: 'high', color: 'bg-orange-100 text-orange-700' },
    { channel: 'Call Center', channelAr: 'مركز الاتصال', target: '< 10 min', priority: 'high', color: 'bg-orange-100 text-orange-700' },
    { channel: 'Social Media', channelAr: 'وسائل التواصل', target: '< 15 min', priority: 'high', color: 'bg-orange-100 text-orange-700' },
    { channel: 'Marketplace', channelAr: 'السوق الإلكتروني', target: '< 15 min', priority: 'high', color: 'bg-orange-100 text-orange-700' },
    { channel: 'Walk-in', channelAr: 'زيارة مباشرة', target: 'Immediate', priority: 'critical', color: 'bg-red-100 text-red-700' },
    { channel: 'Referral', channelAr: 'إحالة', target: '< 30 min', priority: 'medium', color: 'bg-yellow-100 text-yellow-700' },
  ];

  const complianceRules = [
    { en: 'SLA compliance target: >95% across all channels and stages', ar: 'هدف امتثال الاتفاقية: >95% عبر جميع القنوات والمراحل' },
    { en: 'Speed-to-lead: Average < 3 min for digital, < 10 min for other channels', ar: 'سرعة الاستجابة: متوسط <3 دقائق للرقمي، <10 دقائق للقنوات الأخرى' },
    { en: 'SLA timer starts automatically on stage entry', ar: 'يبدأ مؤقت الاتفاقية تلقائيًا عند دخول المرحلة' },
    { en: '80% SLA warning triggers supervisor alert', ar: 'تحذير 80% من الاتفاقية يُفعّل تنبيه المشرف' },
    { en: '100% SLA breach triggers auto-reassignment and manager alert', ar: 'خرق 100% من الاتفاقية يُفعّل إعادة التعيين التلقائي وتنبيه المدير' },
    { en: 'Exception codes must be applied before pausing any SLA timer', ar: 'يجب تطبيق رموز الاستثناء قبل إيقاف أي مؤقت اتفاقية مؤقتًا' },
  ];

  return (
    <div className="space-y-8">
      {/* Compliance Target Banner */}
      <div className="bg-teal-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Clock className="w-8 h-8" />
          <div>
            <div className="text-3xl font-bold">&gt;95%</div>
            <div className="text-teal-200 text-sm">{isAr ? 'هدف الامتثال عبر جميع القنوات والمراحل' : 'SLA Compliance Target – All Channels & Stages'}</div>
          </div>
        </div>
      </div>

      {/* Channel SLA Targets */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-teal-600" />
          {isAr ? 'أهداف اتفاقية مستوى الخدمة حسب القناة' : 'Channel SLA Targets (First Contact)'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {channelTargets.map((ch) => (
            <div key={ch.channel} className="p-4 border border-gray-200 rounded-xl bg-gray-50">
              <div className="text-sm font-semibold text-gray-700 mb-1">{isAr ? ch.channelAr : ch.channel}</div>
              <div className="text-xl font-bold text-gray-900 mb-1">{ch.target}</div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ch.color}`}>{ch.priority}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Stage SLA Targets */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-teal-600" />
          {isAr ? 'أهداف اتفاقية مستوى الخدمة حسب المرحلة' : 'Stage SLA Targets'}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{isAr ? 'المرحلة' : 'Stage'}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{isAr ? 'المالك' : 'Owner'}</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">{isAr ? 'اتفاقية الهدف' : 'Target SLA'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stages.map((stage) => (
                <tr key={stage.meta.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                        style={{ backgroundColor: stage.meta.color || '#6366f1' }}
                      >
                        {stage.meta.order}
                      </span>
                      <Link to={`/pipeline/stage/${stage.meta.id}`} className="font-semibold text-gray-800 hover:text-teal-700">
                        {isAr ? stage.meta.titleAr : stage.meta.title}
                      </Link>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{isAr ? stage.meta.ownerAr : stage.meta.owner}</td>
                  <td className="px-4 py-3 text-teal-700 font-semibold text-xs">{isAr ? stage.meta.slaAr : stage.meta.sla}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Compliance Rules */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-teal-600" />
          {isAr ? 'قواعد الامتثال' : 'Compliance Rules'}
        </h2>
        <div className="space-y-2.5">
          {complianceRules.map((r, i) => (
            <div key={i} className="flex items-start gap-2.5 p-3 bg-teal-50 rounded-lg border border-teal-100">
              <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">{isAr ? r.ar : r.en}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── Governance Policy ───────────────────────────────────────────────────────
function GovernancePolicy({ isAr }: { isAr: boolean }) {
  const roles = getAllRoles();
  const raciMatrix = computeRACIMatrix();

  const auditRequirements = [
    { en: 'All stage transitions must be timestamped and logged', ar: 'جميع انتقالات المرحلة يجب أن تكون بطابع زمني ومسجلة' },
    { en: 'Reason codes required for all outcome transitions', ar: 'رموز الأسباب مطلوبة لجميع انتقالات النتائج' },
    { en: 'SLA breach events auto-logged with agent ID and timestamp', ar: 'أحداث خرق الاتفاقية تُسجل تلقائيًا مع معرّف الوكيل والطابع الزمني' },
    { en: 'All outbound communications logged (call outcome, WhatsApp status)', ar: 'جميع الاتصالات الصادرة مسجلة (نتيجة المكالمة، حالة واتساب)' },
    { en: 'Weekly performance reports generated automatically by CRM', ar: 'تقارير الأداء الأسبوعية تُولَّد تلقائيًا بواسطة CRM' },
    { en: 'Monthly SLA compliance reports reviewed by Sales Manager', ar: 'تقارير الامتثال الشهرية يراجعها مدير المبيعات' },
    { en: 'Quarterly pipeline audit by Quality Analyst role', ar: 'مراجعة ربع سنوية للمنظومة من قِبَل محلل الجودة' },
  ];

  const groupColors: Record<string, string> = {
    bdc: 'bg-blue-100 text-blue-700',
    sales: 'bg-green-100 text-green-700',
    finance: 'bg-yellow-100 text-yellow-700',
    operations: 'bg-orange-100 text-orange-700',
    crm_admin: 'bg-purple-100 text-purple-700',
    quality: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-8">
      {/* Roles Overview */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-slate-600" />
          {isAr ? 'الأدوار والمساءلة' : 'Roles & Accountability'}
          <span className="ml-auto text-sm font-normal text-gray-400">{roles.length} {isAr ? 'أدوار' : 'roles defined'}</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((role) => (
            <Link
              key={role.id}
              to={`/roles/${role.id}`}
              className="p-4 border border-gray-200 rounded-xl hover:border-slate-400 hover:bg-slate-50 transition-all group"
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-800 group-hover:text-slate-700">
                  {isAr ? role.titleAr : role.title}
                </h3>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${groupColors[role.group] || 'bg-gray-100 text-gray-600'}`}>
                  {role.group}
                </span>
              </div>
              <p className="text-xs text-gray-500 line-clamp-2">{isAr ? role.purposeAr : role.purpose}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* RACI Summary */}
      {raciMatrix && raciMatrix.rows && raciMatrix.rows.length > 0 && (
        <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-slate-600" />
            {isAr ? 'مصفوفة RACI – ملخص المراحل' : 'RACI Matrix – Stage Summary'}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-3 py-2 font-semibold text-gray-700">{isAr ? 'المرحلة' : 'Stage'}</th>
                  {raciMatrix.roles.slice(0, 5).map((r) => (
                    <th key={r.id} className="text-center px-2 py-2 font-semibold text-gray-700 whitespace-nowrap">
                      {isAr ? r.titleAr : r.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {raciMatrix.rows.slice(0, 8).map((row) => (
                  <tr key={row.stageId} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {isAr ? row.stageNameAr : row.stageName}
                    </td>
                    {raciMatrix.roles.slice(0, 5).map((r) => {
                      const cell = row.cells.find((c) => c.roleId === r.id);
                      const val = cell?.value;
                      const colors: Record<string, string> = { R: 'bg-blue-100 text-blue-700', A: 'bg-red-100 text-red-700', C: 'bg-yellow-100 text-yellow-700', I: 'bg-gray-100 text-gray-600' };
                      return (
                        <td key={r.id} className="px-2 py-2 text-center">
                          {val ? (
                            <span className={`inline-block w-6 h-6 rounded-full text-xs font-bold leading-6 ${colors[val] || 'bg-gray-100 text-gray-400'}`}>{val}</span>
                          ) : <span className="text-gray-200">–</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-2">R=Responsible A=Accountable C=Consulted I=Informed</p>
        </section>
      )}

      {/* Audit Requirements */}
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-slate-600" />
          {isAr ? 'متطلبات التدقيق والامتثال' : 'Audit & Compliance Requirements'}
        </h2>
        <div className="space-y-2.5">
          {auditRequirements.map((r, i) => (
            <div key={i} className="flex items-start gap-2.5 p-3 bg-slate-50 rounded-lg border border-slate-100">
              <CheckCircle className="w-4 h-4 text-slate-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">{isAr ? r.ar : r.en}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── Policy Config Map ───────────────────────────────────────────────────────
const POLICY_CONFIG: Record<string, {
  titleEn: string; titleAr: string;
  descEn: string; descAr: string;
  icon: React.ElementType; color: string;
  component: React.FC<{ isAr: boolean }>;
}> = {
  'data-quality': {
    titleEn: 'Data Quality & CRM Standards',
    titleAr: 'جودة البيانات ومعايير CRM',
    descEn: 'Mandatory CRM fields, validation rules, and data completeness standards per pipeline stage.',
    descAr: 'حقول CRM الإلزامية وقواعد التحقق ومعايير اكتمال البيانات لكل مرحلة.',
    icon: Database, color: 'blue',
    component: DataQualityPolicy,
  },
  'escalation': {
    titleEn: 'Escalation Policy',
    titleAr: 'سياسة التصعيد',
    descEn: 'SLA breach escalation chains, auto-reassignment rules, and notification channels.',
    descAr: 'سلاسل تصعيد خرق الاتفاقية وقواعد إعادة التعيين التلقائية وقنوات الإشعار.',
    icon: AlertTriangle, color: 'orange',
    component: EscalationPolicy,
  },
  'exceptions': {
    titleEn: 'Exception Handling Policy',
    titleAr: 'سياسة معالجة الاستثناءات',
    descEn: 'SLA exception codes, pause/reset/exempt behaviors, and approval workflows.',
    descAr: 'رموز استثناء الاتفاقية وسلوكيات الإيقاف المؤقت/إعادة التعيين/الإعفاء.',
    icon: Zap, color: 'purple',
    component: ExceptionsPolicy,
  },
  'lead-handling': {
    titleEn: 'Lead Handling & Assignment Policy',
    titleAr: 'سياسة معالجة وتعيين العملاء المحتملين',
    descEn: 'Lead ownership rules, assignment logic, cadence compliance, and handover protocols.',
    descAr: 'قواعد ملكية العملاء المحتملين ومنطق التعيين والامتثال للجداول الزمنية.',
    icon: Users, color: 'green',
    component: LeadHandlingPolicy,
  },
  'sla-compliance': {
    titleEn: 'SLA Compliance Policy',
    titleAr: 'سياسة الامتثال لاتفاقية مستوى الخدمة',
    descEn: 'Channel response time targets, stage duration limits, and compliance tracking.',
    descAr: 'أهداف وقت استجابة القناة وحدود مدة المرحلة وتتبع الامتثال.',
    icon: Clock, color: 'teal',
    component: SLACompliancePolicy,
  },
  'governance': {
    titleEn: 'Governance & Audit Policy',
    titleAr: 'سياسة الحوكمة والمراجعة',
    descEn: 'Role accountability matrix, RACI framework, audit trails, and performance review cadences.',
    descAr: 'مصفوفة مساءلة الأدوار وإطار RACI ومسارات التدقيق وجداول مراجعة الأداء.',
    icon: BookOpen, color: 'slate',
    component: GovernancePolicy,
  },
};

const ICON_COLOR_MAP: Record<string, string> = {
  blue: 'bg-blue-600', orange: 'bg-orange-500', purple: 'bg-purple-600',
  green: 'bg-green-600', teal: 'bg-teal-600', slate: 'bg-slate-600',
};

const POLICY_ORDER = ['data-quality', 'escalation', 'exceptions', 'lead-handling', 'sla-compliance', 'governance'];

// ─── Main Component ──────────────────────────────────────────────────────────
export default function PolicyDetail() {
  const { policyId } = useParams<{ policyId: string }>();
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const policy = policyId ? POLICY_CONFIG[policyId] : null;

  if (!policy) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-700 mb-2">Policy Not Found</h1>
        <p className="text-gray-500 mb-6">The policy "{policyId}" does not exist.</p>
        <Link to="/policies" className="inline-flex items-center gap-2 text-blue-600 hover:underline font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to Policies
        </Link>
      </div>
    );
  }

  const Icon = policy.icon;
  const iconBg = ICON_COLOR_MAP[policy.color] || 'bg-gray-600';
  const PolicyComponent = policy.component;

  // Prev/Next navigation
  const currentIndex = POLICY_ORDER.indexOf(policyId!);
  const prevId = currentIndex > 0 ? POLICY_ORDER[currentIndex - 1] : null;
  const nextId = currentIndex < POLICY_ORDER.length - 1 ? POLICY_ORDER[currentIndex + 1] : null;

  return (
    <div className={`max-w-6xl mx-auto px-4 py-8 ${isAr ? 'rtl' : 'ltr'}`} dir={isAr ? 'rtl' : 'ltr'}>

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/policies" className="hover:text-blue-600 flex items-center gap-1">
          <Shield className="w-4 h-4" />
          {isAr ? 'السياسات والحوكمة' : 'Policies & Governance'}
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-800 font-medium">{isAr ? policy.titleAr : policy.titleEn}</span>
      </nav>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className={`p-3 ${iconBg} rounded-xl flex-shrink-0`}>
            <Icon className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              {isAr ? policy.titleAr : policy.titleEn}
            </h1>
            <p className="text-gray-500 text-sm">
              {isAr ? policy.descAr : policy.descEn}
            </p>
          </div>
        </div>
      </div>

      {/* Policy Content */}
      <PolicyComponent isAr={isAr} />

      {/* Prev / Next Navigation */}
      <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-200">
        {prevId && POLICY_CONFIG[prevId] ? (
          <Link
            to={`/policies/${prevId}`}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-blue-700 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <div>
              <div className="text-xs text-gray-400">{isAr ? 'السابق' : 'Previous'}</div>
              <div>{isAr ? POLICY_CONFIG[prevId].titleAr : POLICY_CONFIG[prevId].titleEn}</div>
            </div>
          </Link>
        ) : <div />}

        <Link to="/policies" className="text-sm text-gray-400 hover:text-gray-600">
          {isAr ? 'جميع السياسات' : 'All Policies'}
        </Link>

        {nextId && POLICY_CONFIG[nextId] ? (
          <Link
            to={`/policies/${nextId}`}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-blue-700 group text-right"
          >
            <div>
              <div className="text-xs text-gray-400">{isAr ? 'التالي' : 'Next'}</div>
              <div>{isAr ? POLICY_CONFIG[nextId].titleAr : POLICY_CONFIG[nextId].titleEn}</div>
            </div>
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        ) : <div />}
      </div>
    </div>
  );
}
